import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { assessmentService } from '../../api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { FileText, ArrowLeft, ChevronRight, Clock, CheckCircle, AlertCircle, User } from 'lucide-react';
import ListPageHeader from '../../components/list/ListPageHeader';
import ListToolbar from '../../components/list/ListToolbar';
import ListPagination from '../../components/list/ListPagination';
import ListEmptyState from '../../components/list/ListEmptyState';
import ListLoadingState from '../../components/list/ListLoadingState';
import DataRow, { DataRowList } from '../../components/list/DataRow';
import StatusPill from '../../components/list/StatusPill';
import { DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers';

function Submissions() {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [userSearch, setUserSearch] = useState('');
    const [assessmentSearch, setAssessmentSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
    const { user } = useAuth();

    const isAdmin = ['admin', 'campus_admin', 'superuser'].includes(user?.user_type);

    useEffect(() => {
        const fetchSubmissions = async () => {
            try {
                const res = await assessmentService.getSubmissions?.()
                    || await import('../../api').then((m) => m.default.get('/assessments/submissions/'));
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setSubmissions(data);
            } catch (err) {
                toast.error('Failed to load submissions');
            } finally {
                setLoading(false);
            }
        };
        fetchSubmissions();
    }, []);

    useEffect(() => {
        setPage(1);
    }, [statusFilter, userSearch, assessmentSearch]);

    const filtered = useMemo(() => {
        let result = submissions;
        if (statusFilter !== 'all') result = result.filter((s) => s.status === statusFilter);
        if (userSearch) {
            const q = userSearch.toLowerCase();
            result = result.filter((s) =>
                s.student_name?.toLowerCase().includes(q)
                || s.student_username?.toLowerCase().includes(q)
                || s.student?.toLowerCase?.()?.includes(q)
            );
        }
        if (assessmentSearch) {
            const q = assessmentSearch.toLowerCase();
            result = result.filter((s) => s.assessment_title?.toLowerCase().includes(q) || s.assessment?.toString().includes(q));
        }
        return result;
    }, [statusFilter, userSearch, assessmentSearch, submissions]);

    const pagedSubmissions = useMemo(
        () => filtered.slice((page - 1) * pageSize, page * pageSize),
        [filtered, page, pageSize]
    );

    const clearFilters = () => {
        setStatusFilter('all');
        setUserSearch('');
        setAssessmentSearch('');
    };

    const statusSegment = (
        <div className="ux-segment">
            {['all', 'pending', 'submitted', 'graded'].map((s) => (
                <button
                    key={s}
                    type="button"
                    className={statusFilter === s ? 'is-active' : ''}
                    onClick={() => setStatusFilter(s)}
                >
                    {s === 'all' ? 'All' : s}
                </button>
            ))}
        </div>
    );

    return (
        <DashboardLayout>
            <main className="page-container content-width ux-list-page">
                <ListPageHeader
                    kicker="Assessments"
                    kickerIcon={FileText}
                    title="Submissions"
                    description={`${filtered.length} result${filtered.length !== 1 ? 's' : ''}`}
                    actions={(
                        <Link to="/assessments" className="ux-btn-sm ux-btn-ghost" style={{ textDecoration: 'none' }}>
                            <ArrowLeft size={14} /> Back
                        </Link>
                    )}
                />

                <ListToolbar filters={statusSegment}>
                    {isAdmin && (
                        <div className="ux-list-search" style={{ flex: '1 1 180px', minWidth: 160 }}>
                            <User size={16} className="ux-list-search-icon" />
                            <input
                                type="search"
                                placeholder="Search student…"
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                            />
                        </div>
                    )}
                    <div className="ux-list-search" style={{ flex: '1 1 180px', minWidth: 160 }}>
                        <FileText size={16} className="ux-list-search-icon" />
                        <input
                            type="search"
                            placeholder="Search assessment…"
                            value={assessmentSearch}
                            onChange={(e) => setAssessmentSearch(e.target.value)}
                        />
                    </div>
                    <button type="button" className="ux-btn-sm ux-btn-ghost" onClick={clearFilters}>
                        Clear
                    </button>
                </ListToolbar>

                {loading ? (
                    <ListLoadingState label="Loading submissions…" />
                ) : pagedSubmissions.length === 0 ? (
                    <ListEmptyState icon={FileText} title="No submissions found" description="Try adjusting your filters." />
                ) : (
                    <DataRowList>
                        {pagedSubmissions.map((submission, index) => (
                            <DataRow
                                key={submission.id}
                                index={index}
                                leading={(
                                    <div className="ux-row-thumb">
                                        <FileText size={18} />
                                    </div>
                                )}
                                title={submission.assessment_title || `Assessment #${submission.assessment}`}
                                subtitle={isAdmin && submission.student_name ? submission.student_name : undefined}
                                meta={(
                                    <>
                                        {submission.submitted_at && (
                                            <span><Clock size={12} /> {new Date(submission.submitted_at).toLocaleDateString()}</span>
                                        )}
                                        {submission.score != null && (
                                            <span style={{ color: submission.is_passed ? 'var(--color-success)' : 'var(--color-error)' }}>
                                                {submission.is_passed ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                                                {' '}Score: {submission.score}%
                                            </span>
                                        )}
                                    </>
                                )}
                                badges={<StatusPill status={submission.status || 'pending'} label={submission.status || 'pending'} />}
                                actions={(
                                    <>
                                        <Link
                                            to={`/assessments/${submission.assessment}/result/${submission.id}`}
                                            className="ux-btn-sm ux-btn-ghost"
                                            style={{ textDecoration: 'none' }}
                                        >
                                            Result <ChevronRight size={14} />
                                        </Link>
                                        {isAdmin && submission.status !== 'pending' && (
                                            <Link
                                                to={`/admin/submissions/${submission.id}/grade`}
                                                className="ux-btn-sm ux-btn-primary"
                                                style={{ textDecoration: 'none' }}
                                            >
                                                Grade
                                            </Link>
                                        )}
                                    </>
                                )}
                            />
                        ))}
                    </DataRowList>
                )}

                {!loading && pagedSubmissions.length > 0 && (
                    <ListPagination
                        page={page}
                        pageSize={pageSize}
                        totalCount={filtered.length}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                    />
                )}
            </main>
        </DashboardLayout>
    );
}

export default Submissions;
